import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getEmployeeById } from '../../services/simpleEmployeeService';
import { getEmployeeBalance, redeemComp } from '../../services/simpleCompService';
import { validateQRData } from '../../services/simpleEmployeeService';
import styles from './ScanResult.module.css';

interface ScanResultProps {
  qrValue: string;
  onBack: () => void;
}

export const ScanResult: React.FC<ScanResultProps> = ({ qrValue, onBack }) => {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  // Validate and parse QR code to get employee ID
  const validation = validateQRData(qrValue);

  // Get employee data
  const { data: employee, isLoading: employeeLoading, error: employeeError } = useQuery({
    queryKey: ['employee-profile', validation.employeeId],
    queryFn: async () => {
      if (!validation.employeeId || !validation.valid) {
        throw new Error('Invalid QR code');
      }
      const result = await getEmployeeById(validation.employeeId);
      if (result.error) throw result.error;
      return result.data;
    },
    enabled: validation.valid && !!validation.employeeId,
    retry: false,
  });

  // Get employee balance
  const { data: balanceResult, isLoading: balanceLoading } = useQuery({
    queryKey: ['employee-balance', employee?.id],
    queryFn: () => employee?.id
      ? getEmployeeBalance(employee.id)
      : Promise.resolve({ data: { drinks_available: 0, food_available: 0 }, error: null }),
    enabled: !!employee?.id,
  });

  const balance = balanceResult?.data || { drinks_available: 0, food_available: 0 };

  // Redemption mutations
  const redeemMutation = useMutation({
    mutationFn: (compType: 'food' | 'drink') => {
      if (!employee?.id) {
        throw new Error('Missing employee');
      }
      return redeemComp(employee.id, compType);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['employee-balance', employee?.id] });
      setError(null);
    },
    onError: (err: any) => {
      setError(err.message || 'Failed to redeem comp');
    }
  });

  const handleRedeem = (type: 'drink' | 'food') => {
    const available = type === 'drink' ? balance.drinks_available : balance.food_available;

    if (available <= 0) {
      setError(`No ${type} comps available`);
      return;
    }

    redeemMutation.mutate(type);
  };

  const handleComplete = () => {
    onBack();
  };

  if (!validation.valid) {
    return (
      <div className={styles.scanResult}>
        <div className={styles.container}>
          <div className={styles.error}>
            <strong>ERROR:</strong> Invalid QR code
          </div>
          <div className={styles.controls}>
            <button onClick={onBack} className={styles.button}>
              BACK TO SCANNER
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (employeeLoading || balanceLoading) {
    return (
      <div className={styles.scanResult}>
        <div className={styles.loading}>
          LOADING EMPLOYEE DATA...
        </div>
      </div>
    );
  }

  if (employeeError || !employee) {
    return (
      <div className={styles.scanResult}>
        <div className={styles.container}>
          <div className={styles.error}>
            <strong>ERROR:</strong> {error || 'Employee not found'}
          </div>
          <div className={styles.controls}>
            <button onClick={onBack} className={styles.button}>
              BACK TO SCANNER
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.scanResult}>
      <div className={styles.container}>
        <div className={styles.header}>
          <h1>SCAN RESULT</h1>
          <p>Employee details ready for redemption</p>
        </div>

        <div className={styles.employeeCard}>
          {employee.photo_url ? (
            <img src={employee.photo_url} alt={employee.name} className={styles.employeePhoto} />
          ) : (
            <div className={styles.employeeAvatar}>
              {employee.name.charAt(0)}
            </div>
          )}

          <div className={styles.employeeName}>
            {employee.name}
          </div>

          <div className={styles.employeeId}>
            ID: {employee.id}
          </div>

          <div className={styles.balances}>
            <div className={styles.balanceItem}>
              <div className={styles.balanceIcon}>🍹</div>
              <div className={`${styles.balanceValue} ${styles.balanceAvailable}`}>
                {balance.drinks_available}
              </div>
              <div className={styles.balanceLabel}>DRINKS</div>
            </div>

            <div className={styles.balanceItem}>
              <div className={styles.balanceIcon}>🍕</div>
              <div className={`${styles.balanceValue} ${styles.balanceAvailable}`}>
                {balance.food_available}
              </div>
              <div className={styles.balanceLabel}>FOOD</div>
            </div>
          </div>
        </div>

        {redeemMutation.isSuccess && (
          <div className={styles.success}>
            ✓ COMP REDEEMED SUCCESSFULLY
          </div>
        )}

        {error && (
          <div className={styles.error}>
            <strong>ERROR:</strong> {error}
          </div>
        )}

        <div className={styles.redeemOptions}>
          <button
            onClick={() => handleRedeem('drink')}
            disabled={redeemMutation.isPending || balance.drinks_available <= 0}
            className={`${styles.redeemButton} drink`}
          >
            🍹 REDEEM DRINK
          </button>

          <button
            onClick={() => handleRedeem('food')}
            disabled={redeemMutation.isPending || balance.food_available <= 0}
            className={`${styles.redeemButton} food`}
          >
            🍕 REDEEM FOOD
          </button>
        </div>

        {(balance.drinks_available <= 0 || balance.food_available <= 0) && (
          <div className={styles.warning}>
            <strong>WARNING:</strong> No comps available for redemption
          </div>
        )}

        <div className={styles.controls}>
          <button
            onClick={handleComplete}
            className={styles.button}
          >
            COMPLETE
          </button>
        </div>
      </div>
    </div>
  );
};
